import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Play, Film, Tv, Sparkles, Calendar, User, Tag, Edit2, Trash2, 
  Eye, Heart, Settings, Plus, ChevronDown, Badge, Star, Clock, X, Volume2, Globe,
  ArrowLeft, Home, Menu
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Badge as UIBadge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import { isCreator } from '../../utils/roleUtils';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

const MyVideosExtended = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [stats, setStats] = useState({
    total: 0,
    ready: 0,
    views: 0,
    duration: 0
  });
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Données de référence pour l'affichage
  const [contentTypes, setContentTypes] = useState([]);
  const [aiModels, setAiModels] = useState([]);
  const [genres, setGenres] = useState([]);

  // Modal lecteur vidéo
  const [videoPlayerModal, setVideoPlayerModal] = useState({
    isOpen: false,
    video: null
  });

  useEffect(() => {
    if (user?.uid) {
      loadVideos();
      loadReferenceData();
    }
  }, [user?.uid]);

  const loadReferenceData = async () => {
    try {
      const [typesRes, modelsRes, genresRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/metadata/content-types`),
        axios.get(`${API_BASE_URL}/api/metadata/ai-models`),
        axios.get(`${API_BASE_URL}/api/metadata/genres`)
      ]);

      setContentTypes(typesRes.data.data || []);
      setAiModels(modelsRes.data.data || []);
      setGenres(genresRes.data.data || []);
    } catch (error) {
      console.error('Erreur chargement données référence:', error);
    }
  };

  const loadVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/videos/user/${user.uid}?limit=50&skip=0`);
      
      if (response.data.success) {
        // Enrichir les données avec les informations complètes
        const enrichedVideos = await enrichVideosData(response.data.data);
        setVideos(enrichedVideos);
        
        // Calculer les statistiques
        calculateStats(enrichedVideos);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Erreur chargement vidéos:', error);
      setError(`Impossible de charger vos vidéos: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const enrichVideosData = async (videosData) => {
    // Enrichir chaque vidéo avec ses genres et informations détaillées
    const enrichedVideos = [];
    
    for (const video of videosData) {
      try {
        // Récupérer les genres de cette vidéo
        const genresRes = await axios.get(`${API_BASE_URL}/api/videos/${video.id}/genres`);
        const videoGenres = genresRes.data.success ? genresRes.data.data : [];

        // Récupérer les informations série/épisode si applicable
        let episodeInfo = null;
        if (video.content_type_id) {
          try {
            const episodeRes = await axios.get(`${API_BASE_URL}/api/videos/${video.id}/episode-info`);
            episodeInfo = episodeRes.data.success ? episodeRes.data.data : null;
          } catch {
            // Ignore si pas d'info épisode
          }
        }

        enrichedVideos.push({
          ...video,
          video_genres: videoGenres,
          episode_info: episodeInfo
        });
      } catch (error) {
        console.error(`Erreur enrichissement vidéo ${video.id}:`, error);
        enrichedVideos.push(video);
      }
    }
    
    return enrichedVideos;
  };

  // Calcul des statistiques
  const calculateStats = (videosData) => {
    const total = videosData.length;
    const ready = videosData.filter(v => v.status === 'published' && v.is_published).length;
    const views = videosData.reduce((sum, v) => sum + (v.views_count || 0), 0);
    const duration = videosData.reduce((sum, v) => sum + (v.duration_seconds || 0), 0);

    setStats({
      total,
      ready,
      views,
      duration: Math.round(duration / 60) // Convertir en minutes
    });
  };

  // Fonctions du lecteur vidéo
  const openVideoPlayer = (video) => {
    setVideoPlayerModal({
      isOpen: true,
      video
    });
  };

  const closeVideoPlayer = () => {
    setVideoPlayerModal({
      isOpen: false,
      video: null
    });
  };

  // Fonction de suppression de vidéo
  const deleteVideo = async (video) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${video.title}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/videos/${video.id}?user_id=${user.uid}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast({
          title: "Vidéo supprimée",
          description: response.data.message,
          duration: 5000,
        });

        // Recharger la liste des vidéos
        await loadVideos();
      } else {
        throw new Error(response.data.message || 'Erreur de suppression');
      }
    } catch (error) {
      console.error('Erreur suppression vidéo:', error);
      toast({
        title: "Erreur de suppression",
        description: error.response?.data?.detail || error.message || 'Erreur lors de la suppression de la vidéo',
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const getContentTypeInfo = (contentTypeId) => {
    return contentTypes.find(t => t.id === contentTypeId) || { label: 'Film', slug: 'movie' };
  };

  const getAiModelInfo = (aiModelId) => {
    return aiModels.find(m => m.id === aiModelId) || { name: 'Modèle IA', version: '' };
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status, isPublished) => {
    if (!isPublished) return 'bg-yellow-500';
    switch (status) {
      case 'processing': return 'bg-blue-500';
      case 'published': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status, isPublished) => {
    if (!isPublished) return 'Brouillon';
    switch (status) {
      case 'processing': return 'En cours';
      case 'published': return 'Publié';
      case 'failed': return 'Échec';
      default: return 'Inconnu';
    }
  };

  // Filtrage des vidéos
  const filteredVideos = videos.filter(video => {
    if (filterType === 'all') return true;
    const contentType = getContentTypeInfo(video.content_type_id);
    return contentType.slug === filterType;
  });

  // Tri des vidéos
  const sortedVideos = [...filteredVideos].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'title':
        return a.title.localeCompare(b.title);
      case 'views':
        return (b.views_count || 0) - (a.views_count || 0);
      default:
        return 0;
    }
  });

  // Fonction pour calculer le temps relatif
  const getRelativeTime = () => {
    const now = new Date();
    const lastUpdate = new Date(Math.max(...videos.map(v => new Date(v.updated_at || v.created_at))));
    const diffInMinutes = Math.floor((now - lastUpdate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'quelques secondes';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} heure${diffInHours > 1 ? 's' : ''}`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} jour${diffInDays > 1 ? 's' : ''}`;
  };

  if (!user || !isCreator(userProfile?.role)) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Accès créateur requis</h1>
          <p className="text-gray-400 mb-6">Vous devez être créateur pour voir vos vidéos</p>
          <Button onClick={() => navigate('/profile')}>
            Devenir créateur
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement de vos créations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-400">Erreur</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={loadVideos}>Réessayer</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Navigation Bar */}
      <div className="sticky top-0 z-40 bg-black/80 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
                title="Retour"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
                title="Accueil"
              >
                <Home className="h-5 w-5" />
              </button>
              <div className="h-6 w-px bg-gray-700"></div>
              <div>
                <h1 className="text-xl font-bold text-white">Mes créations IA</h1>
                <p className="text-sm text-gray-400">{videos.length} contenu{videos.length > 1 ? 's' : ''} créé{videos.length > 1 ? 's' : ''}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/profile')}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
                title="Profil"
              >
                <User className="h-5 w-5" />
              </button>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
                title="Menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header avec stats */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8">
          <div className="flex items-center space-x-4 mb-6 sm:mb-0">
            <div className="bg-pink-600/20 p-4 rounded-2xl">
              <Sparkles className="h-8 w-8 text-pink-400" />
            </div>
            <div>
              <div className="flex items-center space-x-2 text-sm text-gray-400 mb-1">
                <Calendar className="h-4 w-4" />
                <span>Mis à jour il y a {getRelativeTime()}</span>
              </div>
            </div>
          </div>

          <Button 
            onClick={() => navigate('/add')}
            className="bg-pink-600 hover:bg-pink-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau contenu
          </Button>
        </div>

        {/* Cartes de statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Vidéos totales */}
          <div className="bg-gradient-to-br from-pink-600/20 to-pink-800/20 border border-pink-500/30 rounded-2xl p-6 hover:border-pink-400/50 transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-200 text-sm font-medium">Vidéos totales</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.total}</p>
              </div>
              <div className="bg-pink-500/20 p-3 rounded-full">
                <Film className="h-6 w-6 text-pink-400" />
              </div>
            </div>
          </div>

          {/* Prêtes */}
          <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/30 rounded-2xl p-6 hover:border-green-400/50 transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-200 text-sm font-medium">Prêtes</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.ready}</p>
              </div>
              <div className="bg-green-500/20 p-3 rounded-full">
                <Play className="h-6 w-6 text-green-400" />
              </div>
            </div>
          </div>

          {/* Vues totales */}
          <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-500/30 rounded-2xl p-6 hover:border-purple-400/50 transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm font-medium">Vues totales</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.views.toLocaleString()}</p>
              </div>
              <div className="bg-purple-500/20 p-3 rounded-full">
                <Eye className="h-6 w-6 text-purple-400" />
              </div>
            </div>
          </div>

          {/* Minutes de contenu */}
          <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-2xl p-6 hover:border-blue-400/50 transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-200 text-sm font-medium">Minutes de contenu</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.duration}</p>
              </div>
              <div className="bg-blue-500/20 p-3 rounded-full">
                <Clock className="h-6 w-6 text-blue-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Filtres et tri */}
        <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-400">Filtrer:</span>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm"
              >
                <option value="all">Tous les types</option>
                <option value="movie">Films</option>
                <option value="series">Séries</option>
                <option value="anime">Anime</option>
                <option value="manga">Manga</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-400">Trier:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm"
              >
                <option value="recent">Plus récent</option>
                <option value="title">Titre A-Z</option>
                <option value="views">Plus vues</option>
              </select>
            </div>
          </div>

          <div className="text-sm text-gray-400">
            {filteredVideos.length} résultat{filteredVideos.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Grille des vidéos */}
        {sortedVideos.length === 0 ? (
          <div className="text-center py-16">
            <Film className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {filterType === 'all' ? 'Aucun contenu créé' : `Aucun ${filterType} trouvé`}
            </h3>
            <p className="text-gray-400 mb-6">Commencez à partager vos créations IA avec le monde</p>
            <Button onClick={() => navigate('/add')} className="bg-pink-600 hover:bg-pink-700">
              <Plus className="h-4 w-4 mr-2" />
              Créer votre premier contenu
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {sortedVideos.map(video => {
              const contentType = getContentTypeInfo(video.content_type_id);
              const aiModel = getAiModelInfo(video.ai_model_id);
              const videoGenres = video.video_genres || [];

              return (
                <div key={video.id} className="bg-gradient-to-br from-gray-900/80 to-gray-800/60 backdrop-blur-lg border border-gray-700 rounded-2xl overflow-hidden hover:border-pink-500/30 transition-all duration-300 group">
                  {/* Image/Thumbnail */}
                  <div className="relative aspect-video bg-gray-800">
                    <img
                      src={video.poster_url || video.thumbnail_url || '/api/placeholder/640/360'}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Overlay avec statut */}
                    <div className="absolute top-3 left-3 flex items-center space-x-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(video.status, video.is_published)}`}>
                        {getStatusLabel(video.status, video.is_published)}
                      </span>
                      
                      <UIBadge variant="secondary" className="text-xs">
                        {contentType.label}
                      </UIBadge>
                    </div>

                    {/* Bouton play sur hover */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        size="sm"
                        className="bg-white/20 hover:bg-white/30 backdrop-blur-lg"
                        onClick={() => openVideoPlayer(video)}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Lire la vidéo
                      </Button>
                    </div>

                    {/* Durée */}
                    {video.duration_seconds && (
                      <div className="absolute bottom-3 right-3 bg-black/70 px-2 py-1 rounded text-xs text-white">
                        {formatDuration(video.duration_seconds)}
                      </div>
                    )}
                  </div>

                  {/* Contenu */}
                  <div className="p-6 space-y-4">
                    {/* Titre et épisode info */}
                    <div>
                      <h3 className="font-semibold text-white text-lg leading-tight">{video.title}</h3>
                      
                      {/* Badge série/épisode */}
                      {video.episode_info && (
                        <div className="flex items-center space-x-2 mt-2">
                          <UIBadge variant="outline" className="text-xs border-blue-500/50 text-blue-300">
                            <Tv className="h-3 w-3 mr-1" />
                            Saison {video.episode_info.season_number} • Épisode {video.episode_info.episode_number}
                          </UIBadge>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {video.description && (
                      <p className="text-gray-400 text-sm line-clamp-2">
                        {video.description}
                      </p>
                    )}

                    {/* Métadonnées */}
                    <div className="space-y-2">
                      {/* Modèle IA */}
                      <div className="flex items-center space-x-2 text-xs text-gray-400">
                        <Sparkles className="h-3 w-3 text-purple-400" />
                        <span>{aiModel.name}</span>
                        {aiModel.version && (
                          <span className="text-purple-400">v{aiModel.version}</span>
                        )}
                      </div>

                      {/* Date et langue */}
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-3 w-3" />
                          <span>{video.release_year || new Date(video.created_at).getFullYear()}</span>
                        </div>
                        
                        {video.language && (
                          <div className="flex items-center space-x-1">
                            <Globe className="h-3 w-3" />
                            <span className="uppercase">{video.language}</span>
                          </div>
                        )}
                      </div>

                      {/* Créé par */}
                      {video.created_by && (
                        <div className="flex items-center space-x-2 text-xs text-gray-400">
                          <User className="h-3 w-3" />
                          <span>{video.created_by}</span>
                        </div>
                      )}
                    </div>

                    {/* Genres */}
                    {videoGenres.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {videoGenres.slice(0, 3).map(genre => {
                          const genreInfo = genres.find(g => g.id === genre.genre_id);
                          return genreInfo ? (
                            <span key={genre.genre_id} className="px-2 py-1 bg-gray-700/50 rounded text-xs text-gray-300">
                              {genreInfo.name}
                            </span>
                          ) : null;
                        })}
                        {videoGenres.length > 3 && (
                          <span className="px-2 py-1 bg-gray-700/50 rounded text-xs text-gray-400">
                            +{videoGenres.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Stats */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                      <div className="flex items-center space-x-4 text-xs text-gray-400">
                        <div className="flex items-center space-x-1">
                          <Eye className="h-3 w-3" />
                          <span>{video.views_count || 0}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Heart className="h-3 w-3" />
                          <span>{video.likes_count || 0}</span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                          onClick={() => openVideoPlayer(video)}
                        >
                          <Play className="h-3 w-3" />
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                          onClick={() => {/* TODO: Édition */}}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                          onClick={() => deleteVideo(video)}
                          title="Supprimer la vidéo"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                          onClick={() => {/* TODO: Paramètres */}}
                        >
                          <Settings className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Lecteur Vidéo */}
      {videoPlayerModal.isOpen && videoPlayerModal.video && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-6xl bg-gray-900 rounded-2xl overflow-hidden border border-gray-700">
            {/* Header de la modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div className="flex items-center space-x-4">
                <button
                  onClick={closeVideoPlayer}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
                  title="Retour aux créations"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="bg-pink-600/20 p-2 rounded-lg">
                  <Play className="h-5 w-5 text-pink-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">{videoPlayerModal.video.title}</h3>
                  <div className="flex items-center space-x-3 text-sm text-gray-400 mt-1">
                    <span>{getContentTypeInfo(videoPlayerModal.video.content_type_id).label}</span>
                    {videoPlayerModal.video.episode_info && (
                      <>
                        <span>•</span>
                        <span>S{videoPlayerModal.video.episode_info.season_number}E{videoPlayerModal.video.episode_info.episode_number}</span>
                      </>
                    )}
                    {videoPlayerModal.video.duration_seconds && (
                      <>
                        <span>•</span>
                        <span>{formatDuration(videoPlayerModal.video.duration_seconds)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate('/profile')}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
                  title="Profil"
                >
                  <User className="h-5 w-5" />
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
                  title="Accueil"
                >
                  <Home className="h-5 w-5" />
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeVideoPlayer}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Lecteur vidéo */}
            <div className="aspect-video bg-black">
              {videoPlayerModal.video.playback_url ? (
                <video
                  key={videoPlayerModal.video.id}
                  controls
                  autoPlay
                  className="w-full h-full"
                  poster={videoPlayerModal.video.poster_url || videoPlayerModal.video.thumbnail_url}
                >
                  <source src={videoPlayerModal.video.playback_url} type="application/x-mpegURL" />
                  <source 
                    src={videoPlayerModal.video.playback_url.replace('/manifest/video.m3u8', '/mp4')} 
                    type="video/mp4" 
                  />
                  <p className="text-white p-8 text-center">
                    Votre navigateur ne supporte pas la lecture vidéo.
                    <br />
                    <a 
                      href={videoPlayerModal.video.playback_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-pink-400 hover:text-pink-300 underline"
                    >
                      Ouvrir dans un nouvel onglet
                    </a>
                  </p>
                </video>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <Volume2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Vidéo en cours de traitement</p>
                    <p className="text-sm mt-2">Revenez dans quelques minutes</p>
                  </div>
                </div>
              )}
            </div>

            {/* Informations détaillées */}
            <div className="p-6 space-y-4">
              {/* Description */}
              {videoPlayerModal.video.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Description</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {videoPlayerModal.video.description}
                  </p>
                </div>
              )}

              {/* Métadonnées */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-300">Informations</h4>
                  
                  <div className="space-y-2">
                    {/* Modèle IA */}
                    <div className="flex items-center space-x-2 text-xs">
                      <Sparkles className="h-3 w-3 text-purple-400" />
                      <span className="text-gray-400">Modèle :</span>
                      <span className="text-white">
                        {getAiModelInfo(videoPlayerModal.video.ai_model_id).name}
                        {getAiModelInfo(videoPlayerModal.video.ai_model_id).version && 
                          ` v${getAiModelInfo(videoPlayerModal.video.ai_model_id).version}`
                        }
                      </span>
                    </div>

                    {/* Année et langue */}
                    <div className="flex items-center space-x-4 text-xs">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-3 w-3 text-blue-400" />
                        <span className="text-gray-400">Année :</span>
                        <span className="text-white">{videoPlayerModal.video.release_year || 'N/A'}</span>
                      </div>
                      
                      {videoPlayerModal.video.language && (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400">Langue :</span>
                          <span className="text-white uppercase">{videoPlayerModal.video.language}</span>
                        </div>
                      )}
                    </div>

                    {/* Créateur */}
                    {videoPlayerModal.video.created_by && (
                      <div className="flex items-center space-x-2 text-xs">
                        <User className="h-3 w-3 text-green-400" />
                        <span className="text-gray-400">Créé par :</span>
                        <span className="text-white">{videoPlayerModal.video.created_by}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-300">Statistiques</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                      <Eye className="h-4 w-4 text-purple-400 mx-auto mb-1" />
                      <div className="text-lg font-semibold text-white">
                        {videoPlayerModal.video.views_count || 0}
                      </div>
                      <div className="text-xs text-gray-400">Vues</div>
                    </div>
                    
                    <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                      <Heart className="h-4 w-4 text-pink-400 mx-auto mb-1" />
                      <div className="text-lg font-semibold text-white">
                        {videoPlayerModal.video.likes_count || 0}
                      </div>
                      <div className="text-xs text-gray-400">Likes</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Genres */}
              {videoPlayerModal.video.video_genres && videoPlayerModal.video.video_genres.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Genres</h4>
                  <div className="flex flex-wrap gap-2">
                    {videoPlayerModal.video.video_genres.map(genre => {
                      const genreInfo = genres.find(g => g.id === genre.genre_id);
                      return genreInfo ? (
                        <span 
                          key={genre.genre_id} 
                          className="px-3 py-1 bg-gray-700/70 rounded-full text-xs text-gray-300 border border-gray-600"
                        >
                          {genreInfo.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                <div className="flex items-center space-x-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(videoPlayerModal.video.playback_url);
                      toast({
                        title: "Lien copié !",
                        description: "L'URL de la vidéo a été copiée dans le presse-papier"
                      });
                    }}
                  >
                    Copier le lien
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(videoPlayerModal.video.playback_url, '_blank')}
                  >
                    Ouvrir dans un nouvel onglet
                  </Button>
                </div>

                <Button
                  size="sm"
                  onClick={closeVideoPlayer}
                  className="bg-gray-700 hover:bg-gray-600"
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyVideosExtended;